import ComparePage, { CompareRow, CompareFaq, Reason } from '@comp/compare/compare-template'

interface PageContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  reasons: Reason[]
  tableTitle: [string, string]
  tableBody: string
  rows: CompareRow[]
  faqs: CompareFaq[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    seoTitle: 'SailPoint Alternative: Monofor | IGA built into the IdP',
    seoDescription:
      'Looking for a SailPoint alternative? Monosign builds IGA into the identity provider itself: lifecycle automation, certifications, and SoD without a second platform, self-hosted or cloud, at published per-user pricing.',
    heroEyebrow: 'SailPoint alternative',
    heroTitle: ['Enterprise IGA.', 'Without the second platform.'],
    heroBody:
      'SailPoint governs identities that another system authenticates. Monosign is both: the IdP and the governance layer in one product: lifecycle automation, access certifications, segregation of duties, and identity intelligence. Deployed in minutes, priced per user, support included.',
    reasons: [
      {
        title: 'The system that authenticates is the system that governs',
        body: 'SailPoint is governance-only; it needs Okta or Entra ID running next to it, with connectors keeping the two in sync. In Monosign, IGA lives inside the IdP: revoke an entitlement and it is gone at the next login, because SSO, MFA, and governance share one data model.',
      },
      {
        title: 'Governance that reviews itself',
        body: 'Risk-based auto-certification approves the low-risk rows automatically so reviewers spend their attention on the risky ones. Identity intelligence maps effective access and blast radius, and surfaces findings with one-click remediation.',
      },
      {
        title: 'Minutes to production, not a consulting engagement',
        body: 'SailPoint deployments are typically services engagements measured in months. Monosign deploys in minutes, ships 49 ready entitlement connectors with write-back plus generic SCIM, REST, CSV, LDAP, and database connectors, and publishes its entry pricing on the pricing page.',
      },
    ],
    tableTitle: ['Monofor vs SailPoint,', 'feature by feature.'],
    tableBody:
      'SailPoint earned its position as the IGA category leader, and its governance depth is real. The differences are in scope (one platform versus governance-only) and in how much time, services, and budget it takes to get there.',
    rows: [
      {
        feature: 'IGA + IdP in one platform',
        detail: 'The system that authenticates is the system that governs',
        monofor: true,
        competitor: 'IGA only; IdP separate',
      },
      {
        feature: 'JML lifecycle automation',
        detail: 'Joiner, mover, leaver; policy-driven',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Access certification campaigns',
        detail: 'Scheduled, recurring reviews',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Risk-based auto-certification',
        detail: 'Low-risk rows auto-approve; humans review the rest',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Segregation of duties',
        detail: 'Conflicting-access rules enforced on requests and grants',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Entitlement write-back connectors',
        detail: '49 ready connectors plus SCIM, REST, CSV, LDAP, database',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Event automation with rollback',
        detail: 'Provenance-tracked changes can be reverted',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Identity intelligence: effective access & blast radius',
        detail: 'Findings with one-click remediation',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Self-hosted and cloud, same product',
        detail: 'Same features, same price',
        monofor: true,
        competitor: 'Different products',
      },
      {
        feature: 'Deploy in minutes without a services program',
        monofor: true,
        competitor: 'Consulting engagement typical',
      },
      {
        feature: 'Public entry pricing',
        detail: 'From $4 per user / month',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Customer support included',
        monofor: true,
        competitor: 'Tiered',
      },
    ],
    faqs: [
      {
        q: 'Is Monosign a full SailPoint replacement?',
        a: 'For teams that want IdP and IGA in one platform (lifecycle automation, access requests and approvals, certifications, segregation of duties, and reporting), yes. SailPoint leads the standalone-IGA category for a reason: very large enterprises with deep role-mining and analytics programs may still choose it. Monosign fits organizations that want authentication and governance in one product without a services program.',
      },
      {
        q: 'Do we need a separate identity provider next to Monosign?',
        a: 'No, that is the point. SailPoint governs identities, but something else has to authenticate them, which means a second vendor and connectors keeping the two in sync. Monosign is the IdP: SSO, MFA, and governance share one console and one data model, so a revoked entitlement is gone at the next login.',
      },
      {
        q: 'How do access certifications work?',
        a: 'Certification campaigns run on a schedule against the access users actually hold. Risk-based auto-certification approves low-risk rows automatically, so reviewers focus on the entries that carry real risk. Decisions are enforced through connector write-back, and no-SQL reporting with scheduled delivery gives auditors the evidence without anyone writing queries.',
      },
      {
        q: 'What about our existing SailPoint connectors?',
        a: 'Monosign ships 49 ready entitlement connectors with write-back, plus generic SCIM, REST, CSV, LDAP, and database connectors that cover most systems a SailPoint deployment touches. We do not claim parity with SailPoint’s connector catalog; migrations run app by app, and the trial is the right place to validate your specific systems.',
      },
    ],
  },
  tr: {
    seoTitle: 'SailPoint Alternatifi: Monofor | IdP’ye yerleşik IGA',
    seoDescription:
      'SailPoint alternatifi mi arıyorsunuz? Monosign, IGA’yı kimlik sağlayıcının içine yerleştirir: ikinci bir platform olmadan yaşam döngüsü otomasyonu, sertifikasyon ve SoD; kendi altyapınızda veya bulutta, açık kullanıcı başına fiyatlandırmayla.',
    heroEyebrow: 'SailPoint alternatifi',
    heroTitle: ['Kurumsal IGA.', 'İkinci platform olmadan.'],
    heroBody:
      'SailPoint, başka bir sistemin doğruladığı kimlikleri yönetir. Monosign ise her ikisidir: IdP ve yönetişim katmanı tek üründe: yaşam döngüsü otomasyonu, erişim sertifikasyonları, görevler ayrılığı ve kimlik zekâsı. Dakikalar içinde kurulur, kullanıcı başına fiyatlandırılır, destek dahildir.',
    reasons: [
      {
        title: 'Kimliği doğrulayan sistem, yöneten sistemin ta kendisi',
        body: 'SailPoint yalnızca yönetişimdir; yanında Okta veya Entra ID çalışması ve konnektörlerin iki sistemi senkron tutması gerekir. Monosign’da IGA, IdP’nin içinde yaşar: bir yetkiyi kaldırdığınızda bir sonraki oturum açılışında ortadan kalkar; çünkü SSO, MFA ve yönetişim aynı veri modelini paylaşır.',
      },
      {
        title: 'Kendini gözden geçiren yönetişim',
        body: 'Risk tabanlı otomatik sertifikasyon, düşük riskli satırları otomatik onaylar; gözden geçirenler dikkatlerini gerçekten riskli olanlara ayırır. Kimlik zekâsı, etkin erişimi ve etki alanını haritalar; bulguları tek tıkla düzeltme seçeneğiyle sunar.',
      },
      {
        title: 'Danışmanlık projesi değil, dakikalar içinde üretimde',
        body: 'SailPoint kurulumları genellikle aylarla ölçülen hizmet projeleridir. Monosign dakikalar içinde kurulur; geri yazma destekli 49 hazır yetki konnektörünün yanında genel SCIM, REST, CSV, LDAP ve veritabanı konnektörleriyle gelir ve başlangıç fiyatını fiyatlandırma sayfasında açıkça yayımlar.',
      },
    ],
    tableTitle: ['Monofor ve SailPoint,', 'özellik özellik.'],
    tableBody:
      'SailPoint, IGA kategorisinin lideri konumunu hak ederek kazandı; yönetişim derinliği gerçektir. Farklar kapsamda (tek platform ile yalnızca yönetişim arasında) ve oraya varmak için gereken zaman, hizmet ve bütçededir.',
    rows: [
      {
        feature: 'IGA + IdP tek platformda',
        detail: 'Kimliği doğrulayan sistem, yöneten sistemin ta kendisi',
        monofor: true,
        competitor: 'Yalnızca IGA; IdP ayrı',
      },
      {
        feature: 'JML yaşam döngüsü otomasyonu',
        detail: 'İşe giriş, görev değişikliği, ayrılış; politika güdümlü',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Erişim sertifikasyon kampanyaları',
        detail: 'Zamanlanmış, yinelenen gözden geçirmeler',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Risk tabanlı otomatik sertifikasyon',
        detail: 'Düşük riskli satırlar otomatik onaylanır; kalanı insanlar inceler',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Görevler ayrılığı (SoD)',
        detail: 'Çakışan erişim kuralları taleplerde ve atamalarda uygulanır',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Geri yazma destekli yetki konnektörleri',
        detail: '49 hazır konnektör artı SCIM, REST, CSV, LDAP, veritabanı',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Geri alma destekli olay otomasyonu',
        detail: 'Kaynağı izlenen değişiklikler geri alınabilir',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Kimlik zekâsı: etkin erişim ve etki alanı',
        detail: 'Tek tıkla düzeltilebilen bulgular',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Self-hosted ve bulut, aynı ürün',
        detail: 'Aynı özellikler, aynı fiyat',
        monofor: true,
        competitor: 'Farklı ürünler',
      },
      {
        feature: 'Hizmet programı olmadan dakikalar içinde kurulum',
        monofor: true,
        competitor: 'Genellikle danışmanlık projesi gerekir',
      },
      {
        feature: 'Açık başlangıç fiyatı',
        detail: 'Kullanıcı başına aylık 4 $’dan başlar',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Müşteri desteği dahil',
        monofor: true,
        competitor: 'Kademeli',
      },
    ],
    faqs: [
      {
        q: 'Monosign, SailPoint’in yerini tam olarak alabilir mi?',
        a: 'IdP ve IGA’yı tek platformda isteyen ekipler için (yaşam döngüsü otomasyonu, erişim talepleri ve onayları, sertifikasyonlar, görevler ayrılığı ve raporlama) evet. SailPoint bağımsız IGA kategorisine boşuna liderlik etmiyor: derin rol madenciliği ve analitik programları yürüten çok büyük kurumlar yine SailPoint’i seçebilir. Monosign, kimlik doğrulama ile yönetişimi hizmet programına ihtiyaç duymadan tek üründe isteyen kurumlara uygundur.',
      },
      {
        q: 'Monosign’ın yanında ayrı bir kimlik sağlayıcı gerekir mi?',
        a: 'Hayır, mesele tam olarak bu. SailPoint kimlikleri yönetir, ancak onları doğrulayan başka bir sistem gerekir; bu da ikinci bir tedarikçi ve iki sistemi senkron tutan konnektörler demektir. Monosign IdP’nin kendisidir: SSO, MFA ve yönetişim aynı konsolu ve aynı veri modelini paylaşır; kaldırılan bir yetki bir sonraki oturum açılışında ortadan kalkar.',
      },
      {
        q: 'Erişim sertifikasyonları nasıl çalışır?',
        a: 'Sertifikasyon kampanyaları, kullanıcıların fiilen sahip olduğu erişim üzerinde zamanlanmış olarak çalışır. Risk tabanlı otomatik sertifikasyon düşük riskli satırları otomatik onaylar; gözden geçirenler gerçek risk taşıyan kayıtlara odaklanır. Kararlar konnektör geri yazmasıyla uygulanır; zamanlanmış teslimatlı, SQL gerektirmeyen raporlama da denetçilere kanıtı kimse sorgu yazmadan sunar.',
      },
      {
        q: 'Mevcut SailPoint konnektörlerimiz ne olacak?',
        a: 'Monosign, geri yazma destekli 49 hazır yetki konnektörüyle ve bir SailPoint kurulumunun dokunduğu sistemlerin çoğunu kapsayan genel SCIM, REST, CSV, LDAP ve veritabanı konnektörleriyle gelir. SailPoint’in konnektör kataloğuyla birebir eşitlik iddia etmiyoruz; geçişler uygulama uygulama yürütülür ve kendi sistemlerinizi doğrulamanın en doğru yeri deneme sürecidir.',
      },
    ],
  },
}

type Props = { locale: string }

export default function SailPointAlternativePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <ComparePage
      competitor="SailPoint"
      locale={locale}
      bilingual
      path="/compare/sailpoint-alternative"
      seoTitle={c.seoTitle}
      seoDescription={c.seoDescription}
      heroEyebrow={c.heroEyebrow}
      heroTitle={
        <>
          {c.heroTitle[0]}
          <br />
          {c.heroTitle[1]}
        </>
      }
      heroBody={c.heroBody}
      reasons={c.reasons}
      tableTitle={
        <>
          {c.tableTitle[0]}
          <br />
          {c.tableTitle[1]}
        </>
      }
      tableBody={c.tableBody}
      rows={c.rows}
      faqs={c.faqs}
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
